import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import DisplayOnIcon from '@pelckmans/elementis/icons/display-on';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import Pill from '@pelckmans/elementis/components/pill';
import List from '@pelckmans/elementis/components/list';
import { Children, ReactNode, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ExerciseCategoriesContext } from '../../_contexts/ExerciseCategoriesContext';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import { Medialink } from '../../../../api/medialink';
import { MaterialIcon } from '../../components/MaterialIcon';
import { Module } from '../../../../api/module';
import { useRoutes } from '../../../../hooks/useRoutes';
import ActionMenuButton from '../../../../shared-components/ActionMenuButton';
import ModalContext from '../../../../contexts/ModalContext';
import ModuleContext from '../../_contexts/ModuleContext';
import ScorableProgress from '../../toc/components/ScorableProgress';
import useMedialinks from '../../toc/hooks/medialink/useMedialinks';
import ModuleHeaderTag from '../../components/ModuleHeaderTag';
import useAnalytics from '../../../../hooks/useAnalytics';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../constants/analyticsEvents';
import { ValuesOfObject } from '../../../../@types/utils';

interface Props {
  medialink: Medialink;
  onCloseCallback: (() => Promise<void>) | (() => Promise<[void, void]>);
  scorable: boolean;
  pills?: ReactNode;
  subLocationId: ValuesOfObject<typeof ANALYTICS_EVENT_SUBLOCATIONS>;
}

function MedialinkListItem({ medialink, onCloseCallback, scorable, pills, subLocationId }: Props) {
  const { module, allModules } = useContext(ModuleContext);
  const { fetchExerciseCategories } = useContext(ExerciseCategoriesContext);
  const { showModal } = useContext(ModalContext);
  const analytics = useAnalytics();

  const { t } = useTranslation();
  const { subjectSlug, methodSlug } = useParams();

  const { buildAbsolutePath } = useRoutes();
  const { openMedialink } = useMedialinks();

  const nodeHierarchy = getFullHierarchyDescriptionOfNode(medialink.hierarchy, t);

  const fetchData = useCallback(
    async () => Promise.all([fetchExerciseCategories(), onCloseCallback()]),
    [fetchExerciseCategories, onCloseCallback],
  );

  if (!module || !subjectSlug || !methodSlug) return null;

  const medialinkModule = allModules.find(m => m.id === medialink.moduleId);

  if (!medialinkModule) return null;

  async function handleOpenMedialink(medialink: Medialink, medialinkModule: Module) {
    const modal = await openMedialink(
      medialink,
      medialink.hierarchy[medialink.hierarchy.length - 1],
      medialinkModule,
      fetchData,
    );

    analytics.capture({
      eventType: ANALYTICS_EVENT_TYPES.MEDIALINK_ACCESSED,
      objectId: medialink.id,
      subLocationId,
    });

    if (modal) {
      showModal(modal);
    }
  }

  const getDisabled = () => {
    return (
      !medialink.file &&
      !medialink.externalMedia &&
      !medialink.edumaticExercise &&
      !medialink.eduhintExercise &&
      !medialink.assessmentQExercise &&
      !medialink.miniDialog &&
      !medialink.wordList &&
      !medialink.miniSite
    );
  };

  const disabled = getDisabled();

  const nodeLink = buildAbsolutePath(['MATERIAL', 'MATERIAL.CONTENT', 'MATERIAL.CONTENT.TOC'], {
    subjectSlug,
    methodSlug,
    moduleSlug: module.slug,
    selectedModuleSlug: medialinkModule.slug,
    nodeId: medialink.hierarchy[medialink.hierarchy.length - 1].id,
  });

  return (
    <List.Item
      key={medialink.id}
      title={medialink.name}
      disabled={disabled}
      icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
      as="button"
      onClick={() => handleOpenMedialink(medialink, medialinkModule)}
      contentArea={scorable && <ScorableProgress unachievable={medialink.unachievable} score={medialink.score} />}
      actionArea={
        <ActionMenuButton
          disabled={disabled}
          menuProps={{
            ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.A11Y_LABEL'),
          }}
          buttonProps={{
            ariaLabel: t('PAGES.TOC_LEVEL.ACTION_MENU.BUTTON.A11Y_LABEL'),
          }}
        >
          {handleToggle => (
            <>
              <DropdownMenu.Item
                text={t('PAGES.VEM.MATERIAL_SECTION.ACTION_MENU.OPTIONS.OPEN_MATERIAL')}
                textIcon={<DisplayOnIcon data-testid="display-on-icon" />}
                as="button"
                role="menuitem"
                disabled={disabled}
                onClick={() => {
                  handleToggle();
                  handleOpenMedialink(medialink, medialinkModule);
                }}
              />
              <DropdownMenu.Item
                text={t('PAGES.VEM.MATERIAL_SECTION.ACTION_MENU.OPTIONS.OPEN_TOC', { nodeHierarchy })}
                textIcon={<ChapterIcon data-testid="chapter-icon" />}
                as="link"
                role="menuitem"
                showSeparator
                disabled={disabled}
                href={nodeLink}
              />
            </>
          )}
        </ActionMenuButton>
      }
      pillGroupProps={{
        'aria-label': t('PAGES.VEM.MATERIAL_SECTION.MATERIAL.PILL_GROUP.A11Y_LABEL'),
        role: 'group',
        children: [
          <ModuleHeaderTag key={`module-${medialinkModule.id}`} module={medialinkModule} />,
          <Pill key={`toc-${medialink.id}`} icon={<ChapterIcon data-testid="chapter-icon" />}>
            {nodeHierarchy}
          </Pill>,
          ...Children.toArray(pills),
        ],
      }}
    />
  );
}

export default MedialinkListItem;
